import React, { useState, useCallback, useMemo } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button,
  Box, Typography, Chip, Alert, CircularProgress, Divider,
  TextField, FormControlLabel, Checkbox, LinearProgress, Collapse,
  IconButton, List, ListItem, ListItemText, Paper, Tooltip,
  FormControl, InputLabel, Select, MenuItem, ToggleButtonGroup, ToggleButton,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import BoltIcon from '@mui/icons-material/Bolt';
import CategoryIcon from '@mui/icons-material/Category';
import { waveService, SmartWaveGroup, SmartGroupingMode } from '../services/waveService';

interface SmartWaveModalProps {
  open: boolean;
  warehouseId: string;
  onClose: () => void;
  onCreated: () => void;
}

const cohesionColor = (score: number): 'success' | 'warning' | 'error' => {
  if (score >= 70) return 'success';
  if (score >= 40) return 'warning';
  return 'error';
};

const SmartWaveModal: React.FC<SmartWaveModalProps> = ({ open, warehouseId, onClose, onCreated }) => {
  const [orderType, setOrderType] = useState<'Single' | 'Multi'>('Single');
  const [maxBatchSize, setMaxBatchSize] = useState(30);
  const [maxBatchItems, setMaxBatchItems] = useState(60);
  const [prioritizeUrgent, setPrioritizeUrgent] = useState(true);
  const [groupingMode, setGroupingMode] = useState<SmartGroupingMode>('Auto');

  const handleOrderTypeChange = useCallback((_: React.MouseEvent<HTMLElement>, val: 'Single' | 'Multi' | null) => {
    if (!val) return;
    setOrderType(val);
    setGroupingMode(val === 'Multi' ? 'Location' : 'Auto');
    setGroups([]);
    setSelected(new Set());
    setExpanded(new Set());
    setCalculated(false);
    setError('');
  }, []);

  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState('');
  const [calculated, setCalculated] = useState(false);
  const [groups, setGroups] = useState<SmartWaveGroup[]>([]);
  const [totalPending, setTotalPending] = useState(0);
  const [ungrouped, setUngrouped] = useState(0);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [expanded, setExpanded] = useState<Set<number>>(new Set());

  const resetState = useCallback(() => {
    setGroups([]); setSelected(new Set()); setExpanded(new Set());
    setCalculated(false); setError(''); setTotalPending(0); setUngrouped(0);
    setOrderType('Single'); setGroupingMode('Auto');
  }, []);

  const handleClose = useCallback(() => {
    if (loading || creating) return;
    resetState();
    onClose();
  }, [loading, creating, resetState, onClose]);

  const handleCalculate = useCallback(async () => {
    if (!warehouseId) return;
    setLoading(true);
    setError('');
    try {
      const res = await waveService.suggestSmartGrouping({
        WarehouseId: warehouseId,
        MaxBatchSize: maxBatchSize,
        MaxBatchItems: maxBatchItems,
        PrioritizeUrgent: prioritizeUrgent,
        OrderTypeFilter: orderType,
        GroupingMode: groupingMode,
      });
      const data = res.data?.data || res.data;
      const grps: SmartWaveGroup[] = data?.groups || [];
      setGroups(grps);
      setTotalPending(data?.totalPendingOrders ?? 0);
      setUngrouped(data?.ungroupedOrders ?? 0);
      // Varsayılan: yalnızca gerçek batch (>1 sipariş) grupları seçili gelsin.
      setSelected(new Set(grps.filter(g => g.totalOrders > 1).map(g => g.groupIndex)));
      setCalculated(true);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Gruplar hesaplanırken hata oluştu.');
    }
    setLoading(false);
  }, [warehouseId, maxBatchSize, maxBatchItems, prioritizeUrgent, groupingMode, orderType]);

  const toggleSelect = useCallback((idx: number) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  const toggleExpand = useCallback((idx: number) => {
    setExpanded(prev => {
      const next = new Set(prev);
      next.has(idx) ? next.delete(idx) : next.add(idx);
      return next;
    });
  }, []);

  const selectedGroups = useMemo(
    () => groups.filter(g => selected.has(g.groupIndex)),
    [groups, selected]
  );
  const selectedOrderTotal = useMemo(
    () => selectedGroups.reduce((s, g) => s + g.totalOrders, 0),
    [selectedGroups]
  );
  const selectedItemTotal = useMemo(
    () => selectedGroups.reduce((s, g) => s + g.totalItems, 0),
    [selectedGroups]
  );

  const handleCreate = useCallback(async () => {
    if (selectedGroups.length === 0) return;
    setCreating(true);
    setError('');
    let success = 0;
    const failures: string[] = [];
    for (const g of selectedGroups) {
      try {
        await waveService.create({
          WarehouseId: warehouseId,
          OrderIds: g.orders.map(o => o.id),
        });
        success += 1;
      } catch (err: any) {
        failures.push(`Grup ${g.groupIndex + 1}: ${err?.response?.data?.message || 'hata'}`);
      }
    }
    setCreating(false);

    if (failures.length === 0) {
      resetState();
      onCreated();
      return;
    }
    // Kısmi başarı: başarılı olanları yansıt, hataları göster.
    setError(`${success} iş emri oluşturuldu, ${failures.length} başarısız. ${failures.slice(0, 3).join(' | ')}`);
    if (success > 0) onCreated();
  }, [selectedGroups, warehouseId, resetState, onCreated]);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <AutoAwesomeIcon color="secondary" />
        Akıllı İş Emri Oluştur
        <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
          ({orderType === 'Single' ? 'Tekli' : 'Çoklu'} sipariş batch toplama)
        </Typography>
      </DialogTitle>
      <DialogContent dividers>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {/* Sipariş tipi seçimi */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Typography variant="body2" color="text.secondary">Sipariş Tipi:</Typography>
            <ToggleButtonGroup
              value={orderType}
              exclusive
              onChange={handleOrderTypeChange}
              size="small"
            >
              <ToggleButton value="Single">Tekli</ToggleButton>
              <ToggleButton value="Multi">Çoklu</ToggleButton>
            </ToggleButtonGroup>
          </Box>

          {/* Ayarlar */}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
            <FormControl size="small" sx={{ minWidth: 190 }}>
              <InputLabel>Gruplama Tipi</InputLabel>
              <Select
                label="Gruplama Tipi"
                value={groupingMode}
                onChange={e => setGroupingMode(e.target.value as SmartGroupingMode)}
              >
                <MenuItem value="Auto">Otomatik (akıllı)</MenuItem>
                <MenuItem value="Product">Aynı ürün</MenuItem>
                <MenuItem value="VariantFamily">Varyant ailesi</MenuItem>
                <MenuItem value="Category">Kategori</MenuItem>
                <MenuItem value="Location">Konum / koridor</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Max Sipariş / İş Emri"
              type="number"
              size="small"
              value={maxBatchSize}
              onChange={e => setMaxBatchSize(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: 150 }}
              InputProps={{ inputProps: { min: 1 } }}
            />
            <TextField
              label="Max Kalem / İş Emri"
              type="number"
              size="small"
              value={maxBatchItems}
              onChange={e => setMaxBatchItems(Math.max(1, parseInt(e.target.value) || 1))}
              sx={{ width: 150 }}
              InputProps={{ inputProps: { min: 1 } }}
            />
            <FormControlLabel
              control={<Checkbox checked={prioritizeUrgent} onChange={e => setPrioritizeUrgent(e.target.checked)} />}
              label="Acil öne al"
            />
            <Box sx={{ flex: 1 }} />
            <Button variant="contained" color="secondary" onClick={handleCalculate} disabled={loading || creating}>
              {loading ? <CircularProgress size={18} /> : 'Grupları Hesapla'}
            </Button>
          </Box>
          <Alert severity="info" sx={{ py: 0.5 }}>
            {orderType === 'Single' ? (
              <>Yalnızca <strong>tekli siparişler</strong> gruplanır. Otomatik modda öncelik sırası: aynı ürün → varyant ailesi → konum yakınlığı → kategori.</>
            ) : (
              <>Yalnızca <strong>çoklu siparişler</strong> gruplanır. Oluşturulan iş emirleri <strong>konsolidasyonlu toplama</strong> + <strong>raflama</strong> adımlarını içerir.</>
            )}
          </Alert>

          {calculated && (
            <Alert severity="info" sx={{ py: 0.5 }}>
              Toplam <strong>{totalPending}</strong> bekleyen sipariş → <strong>{groups.length}</strong> grup önerildi
              {ungrouped > 0 ? ` (${ungrouped} sipariş gruplanamadı).` : '.'}
            </Alert>
          )}

          {error && <Alert severity="error">{error}</Alert>}

          {calculated && groups.length === 0 && !error && (
            <Alert severity="warning">Gruplanacak bekleyen sipariş bulunamadı.</Alert>
          )}

          {/* Grup listesi */}
          {groups.map(g => {
            const isSel = selected.has(g.groupIndex);
            const isExp = expanded.has(g.groupIndex);
            return (
              <Paper key={g.groupIndex} variant="outlined" sx={{ p: 1.5, borderColor: isSel ? 'secondary.main' : 'divider' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Checkbox checked={isSel} onChange={() => toggleSelect(g.groupIndex)} sx={{ p: 0.5 }} />
                  <Typography variant="subtitle2" fontWeight={700}>Grup {g.groupIndex + 1}</Typography>
                  {g.groupReason && (
                    <Chip label={g.groupReason} color="secondary" variant="outlined" size="small" sx={{ height: 20 }} />
                  )}
                  {g.dominantCategoryName && (
                    <Tooltip title={(g.categoryCount ?? 1) > 1 ? `Baskın kategori (${g.categoryCount} farklı kategori)` : 'Kategori'}>
                      <Chip
                        icon={<CategoryIcon />}
                        label={(g.categoryCount ?? 1) > 1 ? `${g.dominantCategoryName} +${(g.categoryCount ?? 1) - 1}` : g.dominantCategoryName}
                        color="info"
                        variant="outlined"
                        size="small"
                        sx={{ height: 20 }}
                      />
                    </Tooltip>
                  )}
                  {g.hasUrgentOrders && (
                    <Tooltip title="Acil sipariş içeriyor">
                      <Chip icon={<BoltIcon />} label="Acil" color="error" size="small" sx={{ height: 20 }} />
                    </Tooltip>
                  )}
                  <Chip label={`${g.totalOrders} sipariş`} size="small" variant="outlined" sx={{ height: 20 }} />
                  <Chip label={`${g.totalItems} kalem`} size="small" variant="outlined" sx={{ height: 20 }} />
                  {g.estimatedPickDistance != null && (
                    <Tooltip title="Tahmini toplama yayılımı (grid)">
                      <Chip label={`~${g.estimatedPickDistance} br`} size="small" variant="outlined" sx={{ height: 20 }} />
                    </Tooltip>
                  )}
                  <Box sx={{ flex: 1 }} />
                  <Box sx={{ minWidth: 140 }}>
                    <Typography variant="caption" color="text.secondary">Benzerlik: {g.cohesionScore}%</Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min(100, g.cohesionScore)}
                      color={cohesionColor(g.cohesionScore)}
                      sx={{ height: 6, borderRadius: 3 }}
                    />
                  </Box>
                  <IconButton size="small" onClick={() => toggleExpand(g.groupIndex)}>
                    {isExp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                </Box>

                {/* En çok geçen ürünler */}
                {g.topProducts.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 1, pl: 5 }}>
                    {g.topProducts.map(p => (
                      <Tooltip key={p.productId} title={p.categoryName ? `Kategori: ${p.categoryName}` : 'Kategori bilgisi yok'}>
                        <Chip
                          label={`${p.productName || p.stockCode || 'Ürün'}${p.categoryName ? ` (${p.categoryName})` : ''} · ${p.orderCount} sip / ${p.totalQuantity} ad`}
                          size="small"
                          sx={{ height: 22, fontSize: '0.7rem' }}
                        />
                      </Tooltip>
                    ))}
                  </Box>
                )}

                <Collapse in={isExp} unmountOnExit>
                  <Divider sx={{ my: 1 }} />
                  <List dense sx={{ maxHeight: 180, overflow: 'auto' }}>
                    {g.orders.map(o => (
                      <ListItem key={o.id} disablePadding sx={{ py: 0.25 }}>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Typography variant="body2" fontWeight={500}>{o.orderNumber}</Typography>
                              <Typography variant="body2" color="text.secondary">{o.customerName}</Typography>
                              {o.priority >= 8 && <BoltIcon color="error" sx={{ fontSize: 14 }} />}
                            </Box>
                          }
                          secondary={`${o.itemCount} ürün · ${o.orderType === 'Single' ? 'Tekli' : 'Çoklu'}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Collapse>
              </Paper>
            );
          })}
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3 }}>
        <Typography variant="body2" color="text.secondary">
          {calculated && (
            <>Seçili: <strong>{selectedGroups.length}</strong> grup → {selectedOrderTotal} sipariş, {selectedItemTotal} kalem</>
          )}
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={handleClose} disabled={creating}>İptal</Button>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleCreate}
            disabled={creating || loading || selectedGroups.length === 0}
          >
            {creating ? <CircularProgress size={18} /> : `İş Emirlerini Oluştur (${selectedGroups.length})`}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default SmartWaveModal;
